import { useEffect, useMemo, useState } from 'react'

import Button from 'common/components/button'
import Catalog from 'common/components/catalog/Catalog'
import ConfirmationModal from 'common/components/confirmationModal/ConfirmationModal'
import EmptyState from 'common/components/emptyState'
import Input from 'common/components/input'
import Loader from 'common/components/loaders/Loader'
import SingleSelect from 'common/components/singleSelect'
import { errorToast } from 'common/components/toastNotification'
import { formatDate } from 'common/helpers/date'
import { useAppSelector } from 'common/hooks/redux'
import useDebounce from 'common/hooks/useDebounce'
import { Bin as DeleteIcon } from 'common/icons'

import useDeleteProductCatalogMutation from '../api/mutations/useDeleteProductCatalogMutation'
import { ProductCatalogQueryResult } from '../api/queries/useGetProductCatalogCategoriesQuery'
import useSearchProductCatalogQuery from '../api/queries/useSearchProductCatalogQuery'

import SearchTable from './SearchTable'

export type ProductCatalogInfo = {
  name: string
  id: string
  createdAt: string
  lastUpdated: string
  region: string
}

type Props = {
  catalogId: string
  productCatalogs: ProductCatalogInfo[]
  onCatalogChange: (catalogId: string) => void
  onUploadClick: () => void
  onCategorySelect: (ccnPath: string[]) => void
  onProductSelect: (productId: string, categoryId: string) => void
  productCatalog: ProductCatalogQueryResult | undefined
  isLoading: boolean
  onSearchChange: () => void
}

const ProductCatalogTable = ({
  catalogId,
  productCatalogs,
  onCatalogChange,
  onUploadClick,
  onCategorySelect,
  onProductSelect,
  onSearchChange,
  productCatalog,
  isLoading,
}: Props) => {
  const accountId = useAppSelector((state) => state.authStates.accountId)
  const [searchValue, setSearchValue] = useState<string | undefined>()
  const [debouncedSearchValue, setDebouncedSearchValue] =
    useState<string | undefined>()
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [currentPageSize, setCurrentPageSize] = useState<number>(10)

  const debouncedSetSearch = useDebounce((value: string | undefined) => {
    setDebouncedSearchValue(value)
    setCurrentPage(1)
  }, 300)

  useEffect(() => {
    debouncedSetSearch(searchValue)
  }, [searchValue, debouncedSetSearch])

  const isUploading = useAppSelector(
    (state) => state.dataSource.fileStates.product.isUploading
  )

  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState<boolean>(false)

  const deleteProductCatalogMutation = useDeleteProductCatalogMutation()

  const maxDepthCategories = useMemo(() => {
    const getMaxDepth = (obj: Object, depth: number): number => {
      if (typeof obj !== 'object' || obj === null) {
        return depth
      }
      return Object.values(obj).reduce(
        (max, value) => Math.max(max, getMaxDepth(value, depth + 1)),
        depth
      )
    }

    return getMaxDepth(productCatalog?.categories ?? {}, 0)
  }, [productCatalog])

  const selectedProductCatalog = productCatalogs.find(
    (catalog) => catalog.id === catalogId
  )

  const { isFetching: isSearchLoading, data: searchValues } =
    useSearchProductCatalogQuery(
      {
        accountId,
        catalogId,
        region: selectedProductCatalog?.region ?? '',
        query: debouncedSearchValue ?? '',
        page: currentPage,
        pageSize: currentPageSize,
      },
      !!debouncedSearchValue &&
        debouncedSearchValue.length > 0 &&
        !!catalogId &&
        !!selectedProductCatalog?.region &&
        !!accountId
    )

  if (!productCatalog) {
    return null
  }
  return (
    <div>
      {productCatalogs.length > 0 && (
        <Loader
          isLoading={isLoading || (isSearchLoading && !!debouncedSearchValue)}
        >
          <div className="flex gap-4 items-center mb-4 justify-between content-between">
            <div className="flex gap-4 items-center">
              <SingleSelect
                className="max-w-50"
                isDisabled={productCatalogs.length === 1}
                options={productCatalogs.map((catalog) => ({
                  label: catalog.name,
                  value: catalog.id,
                }))}
                value={catalogId}
                onChange={(value) => {
                  onCatalogChange?.(value?.value ?? '')
                  setSearchValue('')
                }}
              />
              <div>Last update: </div>
              <div className="text-gold-500 text-xs">
                {selectedProductCatalog
                  ? formatDate(
                      selectedProductCatalog.lastUpdated ??
                        selectedProductCatalog.createdAt,
                      'd MMM yyyy'
                    )
                  : ''}
              </div>
            </div>
            <div className="flex gap-4 items-center">
              <Button
                variant="icon"
                size="small"
                onClick={() => setIsConfirmDeleteOpen(true)}
                prefixIcon={<DeleteIcon className="w-5 h-5" />}
              />
              <Button variant="primary" onClick={onUploadClick}>
                Upload data
              </Button>
              <Input
                type="search"
                className="w-60"
                placeholder="Filter"
                data-cy="table-widget-search"
                data-testid="table-widget-search"
                value={searchValue}
                onChange={(e) => {
                  setSearchValue(e.target.value)
                  onSearchChange()
                }}
                clear={() => {
                  setSearchValue('')
                  onSearchChange()
                }}
              />
            </div>
          </div>
          <div className="sm:-ml-6 sm:-mr-6 relative">
            {debouncedSearchValue ? (
              <SearchTable
                searchValue={debouncedSearchValue}
                isLoading={isSearchLoading}
                searchResults={searchValues}
                currentPage={currentPage}
                currentPageSize={currentPageSize}
                onPageChange={setCurrentPage}
                onPageSizeChange={(size) => {
                  setCurrentPageSize(size)
                  setCurrentPage(1)
                }}
                onProductSelect={onProductSelect}
              />
            ) : (
              !isUploading && (
                <>
                  {maxDepthCategories > 0 ? (
                    <div className="mx-6">
                      <div className="bg-gold-100 h-15 pl-6 pr-2 py-1 flex items-center text-base-700 text-sm font-medium uppercase leading-tight mb-2">
                        <span>Catalogue list</span>
                      </div>
                      <Catalog
                        items={productCatalog.categories}
                        onSelectItem={onCategorySelect}
                      />
                    </div>
                  ) : (
                    <EmptyState
                      className="mt-6"
                      title="No categories found"
                      description="Please upload a catalog to see the categories."
                    />
                  )}
                </>
              )
            )}
          </div>
        </Loader>
      )}
      <ConfirmationModal
        title="Delete this catalogue?"
        confirmationText="Are you sure you want to delete this catalogue?"
        confirmButtonText="Yes, delete!"
        open={isConfirmDeleteOpen}
        isLoading={deleteProductCatalogMutation.isLoading}
        onCancel={() => setIsConfirmDeleteOpen(false)}
        onConfirm={() => {
          if (!selectedProductCatalog || !accountId) {
            errorToast('Could not delete the catalogue. Missing information.')
            setIsConfirmDeleteOpen(false)
            return
          }
          const deletedCatalogId = selectedProductCatalog.id
          deleteProductCatalogMutation.mutate({
            accountId,
            productCatalogId: deletedCatalogId,
            region: selectedProductCatalog.region,
          })
          setIsConfirmDeleteOpen(false)
          const remainingCatalogs = productCatalogs.filter(
            (catalog) => catalog.id !== deletedCatalogId
          )
          if (remainingCatalogs.length > 0) {
            onCatalogChange(remainingCatalogs[0].id)
          }
        }}
      />
    </div>
  )
}

export default ProductCatalogTable
